import React from 'react'
import { Button, Card, Drawer, Popconfirm, Space, Statistic, Table, Tag, Typography, message } from 'antd'
import { Link } from 'react-router-dom'
import { listShopDues, listShopOrders, collectShopDues } from '../../api/shopDues'
import { formatAmount } from '../../utils/amount'

const { Title, Text } = Typography

export default function ShopDues() {
    const [rows, setRows] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [drawerShop, setDrawerShop] = React.useState(null)
    const [drawerOrders, setDrawerOrders] = React.useState([])
    const [drawerLoading, setDrawerLoading] = React.useState(false)
    const [collectingId, setCollectingId] = React.useState(null)

    const showIds = React.useMemo(() => {
        try { return localStorage.getItem('showIds') === 'true' } catch { return false }
    }, [])

    const fetchDues = React.useCallback(async () => {
        setLoading(true)
        try {
            const data = await listShopDues()
            setRows(data || [])
        } catch (e) {
            console.error(e)
            message.error(e.message || 'فشل في جلب المستحقات')
        } finally {
            setLoading(false)
        }
    }, [])

    React.useEffect(() => { fetchDues() }, [fetchDues])

    async function openOrdersDrawer(shop) {
        setDrawerShop(shop)
        setDrawerOrders([])
        setDrawerLoading(true)
        try {
            const data = await listShopOrders(shop.shopId)
            setDrawerOrders(data || [])
        } catch (e) {
            message.error(e.message || 'فشل في جلب الطلبات')
        } finally {
            setDrawerLoading(false)
        }
    }

    function closeDrawer() {
        setDrawerShop(null)
        setDrawerOrders([])
    }

    async function onCollect(shop) {
        setCollectingId(shop.shopId)
        try {
            const { updatedCount } = await collectShopDues(shop.shopId)
            if (updatedCount > 0) {
                message.success(`تم تحصيل ${updatedCount} سجل`)
            } else {
                message.info('لا توجد سجلات قيد التحصيل لهذا المتجر')
            }
            await fetchDues()
            if (drawerShop && drawerShop.shopId === shop.shopId) {
                await openOrdersDrawer(shop)
            }
        } catch (e) {
            message.error(e.message || 'فشل في التحصيل')
        } finally {
            setCollectingId(null)
        }
    }

    const totalDueOverall = React.useMemo(
        () => rows.reduce((acc, r) => acc + (Number(r.totalDue) || 0), 0),
        [rows]
    )
    const totalPendingOverall = React.useMemo(
        () => rows.reduce((acc, r) => acc + (Number(r.pendingCount) || 0), 0),
        [rows]
    )

    const columns = [
        ...(showIds ? [{ title: 'ID', dataIndex: 'shopId', key: 'shopId', width: 80 }] : []),
        {
            title: 'المتجر', dataIndex: 'shopNameAr', key: 'shopNameAr',
            render: (name, r) => (
                <Space>
                    <Text>{name}</Text>
                    {r.hidden ? <Tag color="default">مخفي</Tag> : null}
                </Space>
            )
        },
        {
            title: 'المستحقات', dataIndex: 'totalDue', key: 'totalDue',
            sorter: (a, b) => Number(a.totalDue) - Number(b.totalDue),
            render: (v) => {
                const n = Number(v) || 0
                return n > 0
                    ? <Text strong style={{ color: '#52c41a' }}>{formatAmount(n)}</Text>
                    : <Text type="secondary">{formatAmount(0)}</Text>
            }
        },
        {
            title: 'سجلات قيد التحصيل', dataIndex: 'pendingCount', key: 'pendingCount',
            sorter: (a, b) => Number(a.pendingCount) - Number(b.pendingCount),
            render: (v) => <Tag color={Number(v) > 0 ? 'gold' : 'default'}>{v}</Tag>
        },
        {
            title: '', key: 'actions',
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openOrdersDrawer(r)}>عرض الطلبات</Button>
                    <Popconfirm
                        title="تحصيل المستحقات"
                        description={`سيتم تحديد ${r.pendingCount} سجل (${formatAmount(r.totalDue)}) كمحصل. متابعة؟`}
                        okText="تحصيل"
                        cancelText="إلغاء"
                        disabled={Number(r.pendingCount) === 0}
                        onConfirm={() => onCollect(r)}
                    >
                        <Button
                            size="small"
                            type="primary"
                            disabled={Number(r.pendingCount) === 0}
                            loading={collectingId === r.shopId}
                        >
                            تحصيل
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ]

    const drawerColumns = [
        {
            title: 'رقم الطلب', dataIndex: 'orderId', key: 'orderId',
            render: (v) => v
                ? <Link to={`/admin/orders/${v}`}><Tag color="blue">#{v}</Tag></Link>
                : '-'
        },
        {
            title: 'التاريخ', dataIndex: 'createdAt', key: 'createdAt',
            render: (v) => v ? new Date(v).toLocaleString('ar-EG') : '-'
        },
        {
            title: 'إجمالي العميل', dataIndex: 'userTotal', key: 'userTotal',
            render: (v) => formatAmount(v)
        },
        {
            title: 'مساهمة في المستحقات', dataIndex: 'revenueFromShop', key: 'revenueFromShop',
            render: (v) => <Text strong style={{ color: '#52c41a' }}>{formatAmount(v)}</Text>
        },
        {
            title: 'الحالة', dataIndex: 'status', key: 'status',
            render: (v) => (
                <Tag color={v === 'collected' ? 'green' : 'gold'}>
                    {v === 'collected' ? 'تم التحصيل' : 'قيد التحصيل'}
                </Tag>
            )
        }
    ]

    return (
        <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 16px', direction: 'rtl' }}>
            <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>مستحقات المتاجر</Title>
                <Button onClick={fetchDues} loading={loading}>تحديث</Button>
            </Space>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
                <Card size="small">
                    <Statistic
                        title="إجمالي المستحقات (كل المتاجر)"
                        value={totalDueOverall}
                        formatter={formatAmount}
                        valueStyle={{ color: '#52c41a' }}
                    />
                </Card>
                <Card size="small">
                    <Statistic title="إجمالي السجلات قيد التحصيل" value={totalPendingOverall} />
                </Card>
            </div>

            <Card bodyStyle={{ padding: 0 }}>
                <Table
                    rowKey="shopId"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 800 }}
                />
            </Card>

            <Drawer
                title={drawerShop ? `${drawerShop.shopNameAr} — مستحقات ${formatAmount(drawerShop.totalDue)}` : 'الطلبات'}
                placement="left"
                width={Math.min(960, typeof window !== 'undefined' ? window.innerWidth - 40 : 960)}
                onClose={closeDrawer}
                open={!!drawerShop}
                styles={{ body: { direction: 'rtl' } }}
            >
                <Table
                    rowKey="financialRecordId"
                    columns={drawerColumns}
                    dataSource={drawerOrders}
                    loading={drawerLoading}
                    pagination={{ pageSize: 20 }}
                    size="small"
                />
            </Drawer>
        </div>
    )
}
